import React, { useEffect, useState } from "react";
import {
    useMediaQuery,
    Box,
    Typography,
    List,
    ListItem,
    ListItemText,
    Paper,
    Pagination,
} from "@mui/material";
import { styled } from "@mui/system";

const formatTimestampPrefix = (prefix) => {
    if (!prefix || !prefix.includes(" ➜ ")) {
        return [{ text: prefix, color: "#808080" }];
    }
    const parts = prefix.split(" ➜ ");
    const dateTimeStringWithTz = parts[0];
    const separator = " ➜ ";
    const match = dateTimeStringWithTz.match(
        /^(\d{4}-\d{2}-\d{2}),\s*(\d{2}:\d{2}:\d{2})\s*([A-Z]{2,3})$/
    );
    if (!match) {
        return [{ text: prefix, color: "#808080" }];
    }
    const [, dateStr, timeStr] = match;
    try {
        const parsableDateTimeString = `${dateStr}T${timeStr}`;
        const dateObj = new Date(parsableDateTimeString);
        if (isNaN(dateObj.getTime())) {
            return [{ text: prefix, color: "#808080" }];
        }
        const displayDate = dateObj.toLocaleDateString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        });
        const displayTime = dateObj.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
        });
        return [
            { text: `${displayDate}, `, color: "#808080" },
            { text: `${displayTime} `, color: "#bbb" },
            { text: separator, color: "#808080" },
        ];
    } catch (e) {
        return [{ text: prefix, color: "#808080" }];
    }
};

const RenderTimestampParts = ({ parts }) => {
    return (
        <>
            {parts.map((part, index) => {
                return (
                    <span key={index} style={{ color: part.color }}>
                        {part.text}
                    </span>
                );
            })}
        </>
    );
};


const LogItem = styled(ListItem)(({ theme }) => ({
    borderBottom: `1px solid #333`,
    "&:last-child": {
        borderBottom: "none",
    },
}));

const UsernameHighlight = styled("span")(({ theme }) => ({
    color: "#98FF98",
    fontWeight: "bold",
}));

const WhiteText = styled("span")(({ theme }) => ({
    color: "#DDDDDD",
}));

const GrayText = styled("span")(({ theme }) => ({
    color: "#808080",
}));

const Logs = () => {
    const [logs, setLogs] = useState([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const rowsPerPage = 50;
    const isMobile = useMediaQuery("(max-width: 600px)");
    const isMobileLarge = useMediaQuery("(max-width: 800px)");
    const isMobileSmall = useMediaQuery("(max-width: 420px)");

    const LogContainer = styled(Paper)(({ theme }) => ({
        backgroundColor: "#060606",
        color: theme.palette.text.primary,
        padding: theme.spacing(2),
        borderRadius: theme.spacing(1),
    }));

    useEffect(() => {
        const fetchLogs = async () => {
            try {
                const response = await fetch(
                    `${process.env.REACT_APP_API_BASE_URL}/logs?page=${page}&limit=${rowsPerPage}`
                );
                const data = await response.json();

                if (response.ok) {
                    const formattedLogs = data.logs.map((log) => ({
                        id: log.id,
                        message: log.message,
                    }));

                    setLogs(formattedLogs);
                    setTotalPages(Math.ceil(data.totalCount / rowsPerPage));
                } else {
                    console.error(data.error || "Failed to fetch logs");
                }
            } catch (error) {
                console.error("Error fetching logs:", error);
            }
        };

        fetchLogs();
    }, [page]);

    return (
        <Box
            sx={{
                width: "100%",
                pt: isMobileLarge ? "16px" : 0,
                pb: isMobileLarge ? "72px" : 1,
            }}
        >
            <LogContainer elevation={3}>
                <Box display="flex" justifyContent="center" my={1}>
                    {logs.length > 0 && (
                        <Pagination
                            count={totalPages}
                            page={page}
                            siblingCount={isMobileSmall ? 0 : 1}
                            boundaryCount={isMobileLarge ? 1 : 4}
                            onChange={(e, value) => setPage(value)}
                            variant="outlined"
                            shape="rounded"
                            size={isMobile ? "medium" : "large"}
                        />
                    )}
                </Box>
                <List sx={{ paddingBottom: 0 }}>
                    {logs.map((log) => {
                        const interactionRegex =
                            /^(.* ➜ )(.+?)( interacted with )(.+)$/;
                        const messageRegex =
                            /^(.* ➜ )(.+?)( sent )(.*)( message to )(.+?): "(.+)"$/;

                        const interactionMatch = log.message.match(interactionRegex);
                        const messageMatch = log.message.match(messageRegex);

                        if (interactionMatch) {
                            const [
                                ,
                                originalTimestampPrefix,
                                username,
                                interactionText,
                                targetUsername,
                            ] = interactionMatch;

                            const timestampParts = formatTimestampPrefix(originalTimestampPrefix);

                            return (
                                <LogItem key={log.id}>
                                    <ListItemText
                                        primary={
                                            <Box>
                                                <Typography variant={isMobile ? "body2" : "body1"} sx={{ display: isMobile ? "block" : "none" }}>
                                                    <Box>
                                                        <RenderTimestampParts parts={timestampParts} isMobileView={isMobile} />
                                                        <UsernameHighlight>{username}</UsernameHighlight>
                                                    </Box>
                                                    <Box>
                                                        <GrayText>{interactionText}</GrayText>
                                                        <WhiteText>{targetUsername}</WhiteText>
                                                    </Box>
                                                </Typography>
                                                <Typography variant={isMobile ? "body2" : "body1"} sx={{ display: !isMobile ? "block" : "none" }}>
                                                    <RenderTimestampParts parts={timestampParts} isMobileView={isMobile} />
                                                    <UsernameHighlight>{username}</UsernameHighlight>
                                                    <GrayText>{interactionText}</GrayText>
                                                    <WhiteText>{targetUsername}</WhiteText>
                                                </Typography>
                                            </Box>
                                        }
                                    />
                                </LogItem>
                            );
                        } else if (messageMatch) {
                            const [
                                ,
                                originalTimestampPrefix,
                                username,
                                actionText,
                                messageType,
                                toText,
                                contactName,
                                messageContent,
                            ] = messageMatch;

                            const timestampParts = formatTimestampPrefix(originalTimestampPrefix);

                            return (
                                <LogItem key={log.id}>
                                    <ListItemText
                                        primary={
                                            <Typography variant={isMobile ? "body2" : "body1"}>
                                                <RenderTimestampParts parts={timestampParts} isMobileView={isMobile} />
                                                <UsernameHighlight>{username}</UsernameHighlight>
                                                <GrayText>{actionText}</GrayText>
                                                <GrayText>{messageType}</GrayText>
                                                <GrayText>{toText}</GrayText>
                                                <WhiteText>{contactName}</WhiteText>
                                                <GrayText>: "{messageContent}"</GrayText>
                                            </Typography>
                                        }
                                    />
                                </LogItem>
                            );
                        }

                        const timestampPartsForFallback = formatTimestampPrefix(log.message.substring(0, log.message.indexOf(" ➜ ") + 3) || log.message);
                        const restOfMessage = log.message.substring(log.message.indexOf(" ➜ ") + 3) || "";

                        if (log.message.includes(" ➜ ")) {
                            return (
                                <LogItem key={log.id}>
                                    <ListItemText
                                        primary={
                                            <Typography variant={isMobile ? "body2" : "body1"}>
                                                <RenderTimestampParts parts={timestampPartsForFallback} isMobileView={isMobile} />
                                                <GrayText>{restOfMessage}</GrayText>
                                            </Typography>
                                        }
                                    />
                                </LogItem>
                            );
                        }

                        return (
                            <LogItem key={log.id}>
                                <ListItemText
                                    primary={
                                        <Typography variant={isMobile ? "body2" : "body1"}>
                                            <GrayText>{log.message}</GrayText>
                                        </Typography>
                                    }
                                />
                            </LogItem>
                        );
                    })}
                </List>
            </LogContainer>
        </Box>
    );
};

export default Logs;
